import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { fmtDateTimeParis, fmtDateParis } from "../lib/date";
import { useAuth } from "../contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Copy, Gift, Sparkles, Star, Wine, X, MessageCircle, Mail, Smartphone, Share2 } from "lucide-react";
import { GOOGLE_REVIEW_URL } from "../lib/links";
import PushToggle from "../components/PushToggle";

const STATUS_FR = {
  confirmed: "Confirmé",
  pending: "En attente",
  pending_payment: "Paiement en attente",
  quoted: "Devis envoyé",
  accepted: "Accepté",
  declined: "Refusé",
  cancelled: "Annulé",
  paid: "Payé",
  completed: "Terminé",
};

const REVIEW_DISMISSED_KEY = "sn_review_banner_dismissed";

export default function Account() {
  const { user } = useAuth();
  const [eventBookings, setEventBookings] = useState([]);
  const [tables, setTables] = useState([]);
  const [privates, setPrivates] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [referral, setReferral] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [reviewDismissed, setReviewDismissed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem(REVIEW_DISMISSED_KEY) === "1"
  );

  const reload = () => {
    api.get("/event-bookings/me").then(({ data }) => setEventBookings(data)).catch(() => {});
    api.get("/table-reservations/me").then(({ data }) => setTables(data)).catch(() => {});
    api.get("/private-events/me").then(({ data }) => setPrivates(data)).catch(() => {});
    api.get("/loyalty/me").then(({ data }) => setLoyalty(data)).catch(() => {});
    api.get("/loyalty/rewards").then(({ data }) => setRewards(data.rewards || [])).catch(() => {});
    api.get("/referral/me").then(({ data }) => setReferral(data)).catch(() => {});
  };

  useEffect(() => { reload(); }, []);

  const hasCompletedOrder = useMemo(() => {
    return (
      eventBookings.some((b) => b.status === "confirmed") ||
      tables.some((t) => ["confirmed", "completed"].includes(t.status)) ||
      privates.some((p) => p.status === "accepted")
    );
  }, [eventBookings, tables, privates]);

  const dismissReviewBanner = () => {
    localStorage.setItem(REVIEW_DISMISSED_KEY, "1");
    setReviewDismissed(true);
  };

  const redeemReward = async (rewardKey) => {
    setRedeeming(true);
    try {
      const { data } = await api.post(`/loyalty/rewards/redeem/${rewardKey}`);
      toast.success(`Récompense débloquée ! Bon ${data.voucher.code} — utilisable au bar via QR code`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setRedeeming(false);
    }
  };

  const redeemPoints = async () => {
    if (!loyalty) return;
    if (loyalty.account.points_balance < loyalty.rules.points_per_voucher) {
      toast.error("Solde insuffisant.");
      return;
    }
    setRedeeming(true);
    try {
      const { data } = await api.post("/loyalty/redeem-points");
      toast.success(`Bon créé : ${data.voucher.code} — ${data.voucher.initial_amount.toFixed(2)} €`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setRedeeming(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copié !`),
      () => toast.error("Impossible de copier")
    );
  };

  if (!user) return null;

  const referralLink = referral?.referral_code
    ? `${window.location.origin}/inscription?ref=${referral.referral_code}`
    : "";

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="account-page">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Mon compte</div>
        <h1 className="font-display text-5xl">Bonjour {user.name?.split(" ")[0]}</h1>
        <p className="text-muted-foreground mt-2">{user.email}</p>
      </div>

      {/* Google Review CTA — appears once user has at least one completed order/event */}
      {hasCompletedOrder && !reviewDismissed && (
        <div
          className="mb-8 rounded-xl border border-primary/40 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-5 flex items-start gap-4"
          data-testid="review-banner"
        >
          <div className="hidden sm:flex w-12 h-12 rounded-full bg-primary/15 items-center justify-center shrink-0">
            <Star className="w-6 h-6 text-primary fill-primary" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.25em] text-primary mb-1">Merci de votre visite</div>
            <h3 className="font-display text-xl mb-1">Vous avez aimé l'expérience ?</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Un avis Google de 30 secondes nous aide énormément — il fait découvrir Saveurs Normandes à de nouveaux passionnés.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button asChild size="sm" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="review-banner-cta">
                <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer">
                  <Star className="w-4 h-4 mr-2 fill-current" /> Laisser un avis Google
                </a>
              </Button>
              <Button size="sm" variant="ghost" onClick={dismissReviewBanner} data-testid="review-banner-dismiss" className="rounded-full">
                Plus tard
              </Button>
            </div>
          </div>
          <button
            onClick={dismissReviewBanner}
            className="text-muted-foreground hover:text-foreground p-1"
            aria-label="Masquer"
            data-testid="review-banner-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <Tabs defaultValue="loyalty" data-testid="account-tabs">
        <TabsList className="bg-card border border-border flex-wrap h-auto">
          <TabsTrigger value="loyalty" data-testid="tab-loyalty">Fidélité</TabsTrigger>
          <TabsTrigger value="referral" data-testid="tab-referral">Parrainage</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">Événements ({eventBookings.length})</TabsTrigger>
          <TabsTrigger value="tables" data-testid="tab-tables">Tables ({tables.length})</TabsTrigger>
          <TabsTrigger value="private" data-testid="tab-private">Bar mobile ({privates.length})</TabsTrigger>
          {(user?.role === "admin" || user?.role === "editor") && (
            <TabsTrigger value="pos-pin" data-testid="tab-pos-pin">Mon PIN POS</TabsTrigger>
          )}
        </TabsList>

        {/* ========= FIDELITE ========= */}
        <TabsContent value="loyalty" className="mt-6" data-testid="loyalty-panel">
          {!loyalty ? (
            <div className="text-muted-foreground">Chargement…</div>
          ) : (
            <div className="grid gap-6">
              <PushToggle />
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Points card */}
                <div className="rounded-xl border border-primary/40 bg-gradient-to-br from-primary/10 to-card p-6" data-testid="loyalty-points-card">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-primary mb-3">
                    <Sparkles className="w-3.5 h-3.5" /> Vos points
                  </div>
                  <div className="font-display text-5xl gold-text">{loyalty.account.points_balance}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Cumul à vie : {loyalty.account.lifetime_points} pts · Échangés : {loyalty.account.points_redeemed} pts
                  </div>
                  <div className="mt-4 text-[11px] text-muted-foreground italic">
                    Chaque euro dépensé = 1 point. Utilisez-les pour débloquer les récompenses ci-dessous.
                  </div>
                </div>

                {/* Cocktails card */}
                <div className="rounded-xl border border-border bg-card p-6" data-testid="loyalty-cocktails-card">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-primary mb-3">
                    <Wine className="w-3.5 h-3.5" /> Cocktails commandés
                  </div>
                  <div className="font-display text-5xl">{loyalty.account.cocktails_total}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Cocktails offerts disponibles : <b className="text-primary">{loyalty.free_cocktails_available}</b>
                    {loyalty.account.free_cocktails_used > 0 &&
                      ` · Utilisés : ${loyalty.account.free_cocktails_used}`}
                  </div>
                  <div className="mt-4">
                    <ProgressBar
                      value={loyalty.account.cocktails_total % loyalty.rules.cocktails_per_free}
                      max={loyalty.rules.cocktails_per_free}
                    />
                    <div className="text-xs text-muted-foreground mt-2">
                      {loyalty.cocktails_to_next_free === 0
                        ? "Bravo ! 1 cocktail offert vient d'être ajouté."
                        : `Encore ${loyalty.cocktails_to_next_free} cocktail(s) pour 1 cocktail offert`}
                    </div>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-4 italic">
                    Les cocktails offerts sont appliqués sur place lors de votre prochaine visite — mentionnez-le simplement à votre arrivée.
                  </div>
                </div>
              </div>

              {/* Reward tiers catalog */}
              {rewards.length > 0 && (
                <div className="rounded-xl border border-border bg-card p-6" data-testid="loyalty-rewards-catalog">
                  <div className="text-xs uppercase tracking-[0.25em] text-primary mb-4 flex items-center gap-2">
                    <Gift className="w-3.5 h-3.5" /> Récompenses à débloquer
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    {rewards.map((r) => (
                      <div
                        key={r.key}
                        className={`rounded-xl border p-4 ${r.affordable ? "border-primary/40 bg-primary/5" : "border-border bg-background/40 opacity-80"}`}
                        data-testid={`reward-${r.key}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <div className="font-display text-lg leading-tight">{r.label}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">{r.description}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-primary font-medium">-{r.value_eur.toFixed(2)} €</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                              {r.cost_points} pts
                            </div>
                          </div>
                        </div>
                        {r.affordable ? (
                          <Button
                            size="sm"
                            onClick={() => redeemReward(r.key)}
                            disabled={redeeming}
                            className="w-full h-10 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                            data-testid={`reward-${r.key}-redeem`}
                          >
                            {redeeming ? "…" : "Débloquer"}
                          </Button>
                        ) : (
                          <div className="text-xs text-muted-foreground text-center py-2">
                            Encore <b className="text-primary">{r.points_needed} pts</b>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground italic mt-4">
                    Une fois débloqué, le bon est utilisable via le QR code de votre table (menu → panier → "Vos récompenses").
                  </p>
                </div>
              )}
              {loyalty.vouchers.length > 0 && (
                <div className="rounded-xl border border-border bg-card p-6" data-testid="loyalty-vouchers">
                  <div className="text-xs uppercase tracking-[0.25em] text-primary mb-4 flex items-center gap-2">
                    <Gift className="w-3.5 h-3.5" /> Bons de réduction actifs
                  </div>
                  <ul className="space-y-2">
                    {loyalty.vouchers.map((v) => (
                      <li key={v.id} className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border border-primary/30 bg-primary/5" data-testid={`voucher-${v.code}`}>
                        <div className="flex items-center gap-3">
                          <code className="font-mono text-base font-semibold tracking-wider text-primary">{v.code}</code>
                          <span className="text-xs text-muted-foreground">expire le {new Date(v.expires_at).toLocaleDateString("fr-FR")}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="gold-text font-display text-lg">−{v.current_balance.toFixed(2)} €</span>
                          <Button size="sm" variant="ghost" onClick={() => copyToClipboard(v.code, "Code")} data-testid={`voucher-copy-${v.code}`}>
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-muted-foreground mt-3">
                    Utilisez votre code lors d'une réservation d'événement ou d'un achat boutique — il s'applique comme une carte cadeau.
                  </p>
                </div>
              )}

              {/* Rules card */}
              <div className="rounded-xl border border-border bg-card/50 p-6" data-testid="loyalty-rules">
                <div className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Comment ça marche ?</div>
                <ul className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                  <li>• <b className="text-foreground">1 € dépensé = 1 point</b> (événements, boutique, cocktails à la table).</li>
                  <li>• <b className="text-foreground">100 pts</b> = bon de réduction de <b className="text-foreground">2,50 €</b> sur votre prochaine réservation.</li>
                  <li>• <b className="text-foreground">10 cocktails</b> commandés = <b className="text-foreground">1 cocktail offert</b> sur place.</li>
                </ul>
              </div>

              {/* History */}
              {loyalty.history.length > 0 && (
                <div className="rounded-xl border border-border bg-card p-6" data-testid="loyalty-history">
                  <div className="text-xs uppercase tracking-[0.25em] text-primary mb-4">Historique</div>
                  <ul className="space-y-1.5 text-sm">
                    {loyalty.history.slice(0, 12).map((h) => (
                      <li key={h.id} className="flex justify-between gap-3 border-t border-border/40 pt-2 first:border-0 first:pt-0 text-muted-foreground">
                        <span className="text-xs">
                          {new Date(h.created_at).toLocaleDateString("fr-FR")} · {h.note}
                        </span>
                        <span className="text-xs">
                          {h.delta_points !== 0 && (
                            <span className={h.delta_points > 0 ? "text-primary" : ""}>
                              {h.delta_points > 0 ? "+" : ""}{h.delta_points} pts
                            </span>
                          )}
                          {h.delta_cocktails !== 0 && (
                            <span className="ml-2">+{h.delta_cocktails} 🍸</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* ========= PARRAINAGE ========= */}
        <TabsContent value="referral" className="mt-6" data-testid="referral-panel">
          {!referral ? (
            <div className="text-muted-foreground">Chargement…</div>
          ) : (
            <div className="grid gap-6">
              <div className="rounded-xl border border-primary/40 bg-gradient-to-br from-primary/10 to-card p-6">
                <div className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Votre code de parrainage</div>
                <div className="flex items-center gap-3 flex-wrap">
                  <code
                    className="font-mono text-3xl font-semibold tracking-[0.3em] gold-text bg-card border border-border rounded-lg px-5 py-2"
                    data-testid="referral-code"
                  >
                    {referral.referral_code}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(referral.referral_code, "Code")}
                    className="rounded-full"
                    data-testid="referral-code-copy"
                  >
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copier
                  </Button>
                </div>

                <div className="mt-5">
                  <div className="text-xs text-muted-foreground mb-2">Lien à partager</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-xs sm:text-sm break-all bg-input border border-border rounded-lg px-3 py-2 flex-1 min-w-0" data-testid="referral-link">
                      {referralLink}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(referralLink, "Lien")}
                      className="rounded-full"
                      data-testid="referral-link-copy"
                    >
                      <Copy className="w-3.5 h-3.5 mr-1" /> Copier
                    </Button>
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-3 mt-6">
                  <Stat label="Filleuls actifs" value={referral.referrals_count} testid="referral-stat-count" />
                  <Stat
                    label="Vos avantages"
                    value={referral.referrer_perk_active ? `−${referral.parrain_discount_pct}%` : "—"}
                    sub={referral.referrer_perk_active ? "sur vos prochains cocktails" : "Activé après 1 filleul"}
                    testid="referral-stat-parrain"
                  />
                  <Stat
                    label="Bonus filleul"
                    value={`−${referral.welcome_discount_pct}%`}
                    sub="sur sa 1re commande"
                    testid="referral-stat-welcome"
                  />
                </div>
              </div>

              {/* Share widgets */}
              <ShareWidgets
                referralLink={referralLink}
                code={referral.referral_code}
                welcomePct={referral.welcome_discount_pct}
              />

              <div className="rounded-xl border border-border bg-card/50 p-6" data-testid="referral-rules">
                <div className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Comment ça marche ?</div>
                <ul className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                  <li>1. Partagez votre code ou votre lien personnalisé.</li>
                  <li>2. Votre filleul crée son compte avec votre code → il reçoit <b className="text-foreground">−{referral.welcome_discount_pct}%</b> sur sa 1<sup>re</sup> commande (événement ou boutique).</li>
                  <li>3. Dès qu'il s'inscrit, vous bénéficiez de <b className="text-foreground">−{referral.parrain_discount_pct}%</b> sur vos prochains cocktails (réservations événements & boutique) — à vie.</li>
                </ul>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ========= EVENTS ========= */}
        <TabsContent value="events" className="mt-6">
          {eventBookings.length === 0 ? (
            <div className="text-muted-foreground">Aucune réservation d'événement.</div>
          ) : (
            <div className="grid gap-4">
              {eventBookings.map((b) => (
                <div key={b.id} className="rounded-xl border border-border bg-card p-6 flex justify-between items-start" data-testid={`booking-event-${b.id}`}>
                  <div>
                    <div className="font-display text-2xl">{b.event_title || "Événement"}</div>
                    <div className="text-sm text-muted-foreground">
                      {b.event_date ? fmtDateTimeParis(b.event_date) : ""}
                    </div>
                    <div className="text-sm mt-2">{b.num_seats} place(s) · <span className="gold-text">{b.total_price.toFixed(2)} €</span></div>
                    {b.gift_card_applied > 0 && (
                      <div className="text-xs text-primary mt-1" data-testid={`gift-applied-${b.id}`}>
                        🎁 Carte cadeau appliquée : −{b.gift_card_applied.toFixed(2)} €
                      </div>
                    )}
                    {b.referral_discount_amount > 0 && (
                      <div className="text-xs text-primary mt-1" data-testid={`referral-applied-${b.id}`}>
                        ✨ Parrainage : −{b.referral_discount_amount.toFixed(2)} € ({b.referral_discount_pct}% {b.referral_discount_kind === "welcome" ? "bienvenue" : "parrain"})
                      </div>
                    )}
                    {b.snacks && b.snacks.length > 0 && (
                      <ul className="mt-3 text-xs text-muted-foreground space-y-0.5">
                        {b.snacks.map((s, i) => (
                          <li key={`${b.id}-${s.name}-${i}`}>+ {s.quantity} × {s.name} ({s.line_total.toFixed(2)} €)</li>
                        ))}
                      </ul>
                    )}
                    {b.is_gift && b.status === "confirmed" && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/event-bookings/${b.id}/egift.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-4 text-xs uppercase tracking-[0.15em] rounded-full border border-primary/50 px-4 py-2 hover:bg-primary hover:text-primary-foreground transition-colors"
                        data-testid={`download-egift-${b.id}`}
                      >
                        🎁 Télécharger l&apos;e-cadeau (PDF)
                      </a>
                    )}
                  </div>
                  <span className="text-xs uppercase tracking-[0.2em] px-3 py-1 rounded-full border border-border">{STATUS_FR[b.status] || b.status}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="tables" className="mt-6">
          {tables.length === 0 ? (
            <div className="text-muted-foreground">Aucune réservation de table.</div>
          ) : (
            <div className="grid gap-4">
              {tables.map((t) => (
                <div key={t.id} className="rounded-xl border border-border bg-card p-6 flex justify-between items-start" data-testid={`booking-table-${t.id}`}>
                  <div>
                    <div className="font-display text-2xl">Table · {t.num_guests} personne(s)</div>
                    <div className="text-sm text-muted-foreground">{fmtDateParis(t.date)} · {t.time_slot}</div>
                    {t.snack_selection?.length > 0 && (
                      <div className="text-xs text-muted-foreground mt-2">Tapas : {t.snack_selection.join(", ")}</div>
                    )}
                    {t.cocktail_preorder?.length > 0 && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Cocktails pré-commandés : {t.cocktail_preorder.reduce((s, c) => s + (c.quantity || 0), 0)}
                      </div>
                    )}
                  </div>
                  <span className="text-xs uppercase tracking-[0.2em] px-3 py-1 rounded-full border border-border">{STATUS_FR[t.status] || t.status}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="private" className="mt-6">
          {privates.length === 0 ? (
            <div className="text-muted-foreground">Aucune demande de bar mobile.</div>
          ) : (
            <div className="grid gap-4">
              {privates.map((p) => (
                <div key={p.id} className="rounded-xl border border-border bg-card p-6" data-testid={`booking-private-${p.id}`}>
                  <div className="flex justify-between items-start gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-display text-2xl capitalize">{p.event_type.replace("_", " ")}</div>
                      <div className="text-sm text-muted-foreground">{fmtDateParis(p.preferred_date)} · {p.location}</div>
                      <div className="text-sm mt-2">{p.num_guests} invité(s)</div>
                    </div>
                    <span className="text-xs uppercase tracking-[0.2em] px-3 py-1 rounded-full border border-border">{STATUS_FR[p.status] || p.status}</span>
                  </div>
                  {p.quote && (
                    <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-4 flex justify-between items-center flex-wrap gap-3" data-testid={`account-quote-${p.id}`}>
                      <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-primary">Devis {p.quote.number}</div>
                        <div className="text-sm mt-1">TTC : <span className="gold-text font-display text-lg">{p.quote.amount_ttc.toFixed(2)} €</span></div>
                        <div className="text-[11px] text-muted-foreground mt-1">Consultez votre email pour le PDF détaillé.</div>
                      </div>
                    </div>
                  )}
                  {p.admin_notes && (
                    <div className="text-xs text-muted-foreground mt-3 italic">Note : {p.admin_notes}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {(user?.role === "admin" || user?.role === "editor") && (
          <TabsContent value="pos-pin" className="mt-6" data-testid="pos-pin-panel">
            <MyPosPinPanel />
          </TabsContent>
        )}
      </Tabs>

      {/* Permanent Google review footer link */}
      <div className="mt-12 text-center text-sm text-muted-foreground">
        Vous avez aimé ?{" "}
        <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="account-review-link">
          Laissez-nous un avis Google
        </a>
        {" "}— ça nous aide énormément.
      </div>
    </div>
  );
}

function ProgressBar({ value, max }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="h-2 rounded-full bg-input overflow-hidden" data-testid="progress-bar">
      <div
        className="h-full bg-gradient-to-r from-primary to-primary/60 transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function Stat({ label, value, sub, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card/60 p-4 text-center" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{label}</div>
      <div className="font-display text-2xl gold-text">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function ShareWidgets({ referralLink, code, welcomePct }) {
  const message = `Salut ! Je te recommande Saveurs Normandes — bar à cocktails & rhums arrangés primés à Louviers. Avec mon code "${code}", tu reçois ${welcomePct}% sur ta 1re commande : ${referralLink}`;
  const encoded = encodeURIComponent(message);
  const subject = encodeURIComponent("Une adresse à découvrir à Louviers");

  const whatsappUrl = `https://wa.me/?text=${encoded}`;
  const smsUrl = `sms:?&body=${encoded}`;
  const mailUrl = `mailto:?subject=${subject}&body=${encoded}`;

  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const nativeShare = async () => {
    try {
      await navigator.share({
        title: "Saveurs Normandes",
        text: `Mon code parrain : ${code} — ${welcomePct}% sur ta 1re commande.`,
        url: referralLink,
      });
    } catch {
      /* user cancelled */
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="referral-share">
      <div className="text-xs uppercase tracking-[0.25em] text-primary mb-2 flex items-center gap-2">
        <Share2 className="w-3.5 h-3.5" /> Partager mon code
      </div>
      <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
        Message pré-rempli — modifiez-le si vous le souhaitez avant l'envoi.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[#25D366] text-white font-medium text-sm hover:opacity-90 transition-opacity"
          data-testid="share-whatsapp"
        >
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </a>
        <a
          href={smsUrl}
          className="inline-flex items-center justify-center gap-2 h-11 rounded-full border border-border bg-input hover:border-primary/50 text-sm transition-colors"
          data-testid="share-sms"
        >
          <Smartphone className="w-4 h-4" /> SMS
        </a>
        <a
          href={mailUrl}
          className="inline-flex items-center justify-center gap-2 h-11 rounded-full border border-border bg-input hover:border-primary/50 text-sm transition-colors"
          data-testid="share-mail"
        >
          <Mail className="w-4 h-4" /> Email
        </a>
        {canNativeShare ? (
          <button
            type="button"
            onClick={nativeShare}
            className="inline-flex items-center justify-center gap-2 h-11 rounded-full border border-primary/60 text-primary hover:bg-primary hover:text-primary-foreground text-sm transition-colors"
            data-testid="share-native"
          >
            <Share2 className="w-4 h-4" /> Plus
          </button>
        ) : (
          <button
            type="button"
            onClick={() => navigator.clipboard.writeText(message).then(
              () => toast.success("Message copié !"),
              () => toast.error("Impossible de copier")
            )}
            className="inline-flex items-center justify-center gap-2 h-11 rounded-full border border-primary/60 text-primary hover:bg-primary hover:text-primary-foreground text-sm transition-colors"
            data-testid="share-copy-msg"
          >
            <Copy className="w-4 h-4" /> Copier le message
          </button>
        )}
      </div>
      <details className="mt-4 group" data-testid="share-preview">
        <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground select-none">
          Aperçu du message
        </summary>
        <p className="mt-2 text-xs text-muted-foreground italic leading-relaxed bg-input border border-border rounded-lg p-3">
          {message}
        </p>
      </details>
    </div>
  );
}


// ==================== MY POS PIN PANEL ====================
function MyPosPinPanel() {
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasPin, setHasPin] = useState(false);

  useEffect(() => {
    api.get("/auth/pos/my-pin-status")
      .then(({ data }) => setHasPin(!!data?.has_pin))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(pin)) return toast.error("Le PIN doit contenir 4 chiffres");
    if (pin !== pin2) return toast.error("Les deux PIN ne correspondent pas");
    if (!password) return toast.error("Confirmez votre mot de passe");
    setSubmitting(true);
    try {
      await api.post("/auth/pos/set-pin", { pin, current_password: password });
      toast.success("PIN mis à jour — utilisez-le sur la tablette POS");
      setHasPin(true);
      setPin(""); setPin2(""); setPassword("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Supprimer votre PIN POS ? Vous devrez en définir un nouveau pour vous connecter sur la tablette.")) return;
    try {
      await api.delete("/auth/pos/pin");
      toast.success("PIN supprimé");
      setHasPin(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div className="max-w-lg space-y-4" data-testid="my-pos-pin">
      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="font-display text-2xl mb-1">Mon PIN POS</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {hasPin
            ? "Un PIN est déjà configuré. Vous pouvez le changer ci-dessous ou le supprimer."
            : "Définissez un code à 4 chiffres pour vous connecter sur la tablette du bar sans email/mot de passe. Session valable 12 h."}
        </p>
        <form onSubmit={submit} className="grid gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Nouveau PIN (4 chiffres)</label>
            <input
              type="password" inputMode="numeric" pattern="\d{4}" maxLength={4}
              value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="w-full h-12 rounded-md border border-border bg-background px-3 tracking-[0.5em] text-center font-display text-2xl"
              data-testid="pos-pin-input" required
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Confirmez le PIN</label>
            <input
              type="password" inputMode="numeric" pattern="\d{4}" maxLength={4}
              value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="w-full h-12 rounded-md border border-border bg-background px-3 tracking-[0.5em] text-center font-display text-2xl"
              data-testid="pos-pin-input-confirm" required
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Mot de passe actuel</label>
            <input
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full h-12 rounded-md border border-border bg-background px-3"
              data-testid="pos-pin-password" required
            />
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={submitting}
                    className="flex-1 h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium disabled:opacity-50"
                    data-testid="pos-pin-save">
              {submitting ? "Enregistrement…" : (hasPin ? "Mettre à jour" : "Définir le PIN")}
            </button>
            {hasPin && (
              <button type="button" onClick={clear}
                      className="h-12 px-4 rounded-full border border-destructive/40 text-destructive hover:bg-destructive/5"
                      data-testid="pos-pin-clear">
                Supprimer
              </button>
            )}
          </div>
        </form>
      </div>
      <div className="rounded-xl border border-border/40 bg-card/40 p-4 text-xs text-muted-foreground">
        <strong className="text-foreground">Sécurité :</strong> après 5 tentatives ratées, le PIN est verrouillé 15 min.
        Un PIN POS ne donne accès qu'à la tablette (/pos) — pas aux données admin sensibles.
      </div>
    </div>
  );
}

